import { useTranslation } from 'react-i18next';
import { CalendarPickerButton } from '../booking/CalendarPickerButton';
import { formatReservationDatetime } from '../../utils/formatReservationDatetime';
import '../PaymentPage.css';

export type ConfirmationModuleType =
  | 'booking'
  | 'gym_plan'
  | 'gym_bono'
  | 'gym_clase_particular';

export interface ReservationInfo {
  serviceName?: string;
  startIso: string;
  endIso?: string;
  resourceName?: string;
  bookingId?: string;
}

export interface PurchaseInfo {
  name: string;
  details?: string;
}

export interface WebpaymentConfirmationProps {
  moduleType: ConfirmationModuleType;
  paymentCompleted: boolean;
  /** Override auto-derived title */
  title?: string;
  /** Override auto-derived description */
  description?: string;
  reservation?: ReservationInfo;
  purchase?: PurchaseInfo;
  showAddToCalendar?: boolean;
  language?: string;
  tenantId?: string;
  onPrimaryAction?: () => void;
  onBackToMenu?: () => void;
  /** Override del texto del botón principal (por defecto según moduleType). */
  primaryLabel?: string;
  /** Override del texto del botón secundario (por defecto "volver al menú"). */
  secondaryLabel?: string;
}

// i18n key maps
const TITLE_KEYS: Record<ConfirmationModuleType, Record<'paid' | 'free', string>> = {
  booking:               { free: 'webpayment.confirm.booking_free_title',      paid: 'webpayment.confirm.booking_paid_title' },
  gym_plan:              { free: 'webpayment.confirm.gym_plan_title',           paid: 'webpayment.confirm.gym_plan_title' },
  gym_bono:              { free: 'webpayment.confirm.gym_bono_title',           paid: 'webpayment.confirm.gym_bono_title' },
  gym_clase_particular:  { free: 'webpayment.confirm.gym_clase_free_title',     paid: 'webpayment.confirm.gym_clase_paid_title' },
};

const DESC_KEYS: Record<ConfirmationModuleType, Record<'paid' | 'free', string>> = {
  booking:               { free: 'webpayment.confirm.booking_free_description',      paid: 'webpayment.confirm.booking_paid_description' },
  gym_plan:              { free: 'webpayment.confirm.gym_plan_description',           paid: 'webpayment.confirm.gym_plan_description' },
  gym_bono:              { free: 'webpayment.confirm.gym_bono_description',           paid: 'webpayment.confirm.gym_bono_description' },
  gym_clase_particular:  { free: 'webpayment.confirm.gym_clase_free_description',     paid: 'webpayment.confirm.gym_clase_paid_description' },
};

const DETAILS_HEADING_KEYS: Record<ConfirmationModuleType, string> = {
  booking:              'webpayment.confirm.reservation_details',
  gym_plan:             'webpayment.confirm.subscription_details',
  gym_bono:             'webpayment.confirm.bundle_details',
  gym_clase_particular: 'webpayment.confirm.reservation_details',
};

const PRIMARY_BTN_KEYS: Record<ConfirmationModuleType, string> = {
  booking:              'webpayment.confirm.btn_my_reservations',
  gym_plan:             'webpayment.confirm.btn_my_subscription',
  gym_bono:             'webpayment.confirm.btn_my_bundles',
  gym_clase_particular: 'webpayment.confirm.btn_my_reservations',
};

const isReservationType = (m: ConfirmationModuleType) =>
  m === 'booking' || m === 'gym_clase_particular';

export function WebpaymentConfirmation({
  moduleType,
  paymentCompleted,
  title: titleOverride,
  description: descOverride,
  reservation,
  purchase,
  showAddToCalendar = false,
  language = 'es',
  tenantId,
  onPrimaryAction,
  onBackToMenu,
  primaryLabel: primaryLabelOverride,
  secondaryLabel: secondaryLabelOverride,
}: WebpaymentConfirmationProps) {
  const { t } = useTranslation();
  const tr = (key: string) => t(key, { lng: language });

  const paymentKey = paymentCompleted ? 'paid' : 'free';
  const title = titleOverride ?? tr(TITLE_KEYS[moduleType][paymentKey]);
  const description = descOverride ?? tr(DESC_KEYS[moduleType][paymentKey]);
  const detailsHeading = tr(DETAILS_HEADING_KEYS[moduleType]);
  const primaryLabel = primaryLabelOverride ?? tr(PRIMARY_BTN_KEYS[moduleType]);
  const secondaryLabel = secondaryLabelOverride ?? tr('webpayment.confirm.btn_back_to_menu');

  const formattedDatetime =
    reservation?.startIso
      ? formatReservationDatetime(reservation.startIso, reservation.endIso, language)
      : null;

  const hasDetails =
    isReservationType(moduleType)
      ? Boolean(reservation?.serviceName || formattedDatetime || reservation?.resourceName)
      : Boolean(purchase?.name);

  return (
    <div className="payment-content wpi-confirm-in">
      <div className="success-icon" aria-hidden="true">✓</div>
      <h2>{title}</h2>
      <p>{description}</p>

      {hasDetails && (
        <div className="payment-booking-preview">
          <p className="payment-booking-preview-title">{detailsHeading}</p>

          {isReservationType(moduleType) ? (
            <>
              {reservation?.serviceName && (
                <p className="payment-booking-preview-line">
                  <strong>{reservation.serviceName}</strong>
                </p>
              )}
              {formattedDatetime && (
                <p className="payment-booking-preview-line">{formattedDatetime}</p>
              )}
              {reservation?.resourceName && (
                <p className="payment-booking-preview-line">{reservation.resourceName}</p>
              )}
              <p className="payment-booking-preview-hint">
                {tr('payment.result.email_hint')}
              </p>
              {showAddToCalendar && reservation?.startIso && reservation?.endIso && (
                <div style={{ marginTop: 10 }}>
                  <CalendarPickerButton
                    uid={reservation.bookingId ?? 'booking'}
                    serviceName={reservation.serviceName ?? 'Reserva'}
                    slotStart={reservation.startIso}
                    slotEnd={reservation.endIso}
                    filename={`reserva-${reservation.bookingId ?? 'booking'}.ics`}
                    label={tr('payment.result.add_to_calendar')}
                    labelGoogle="Google Calendar"
                    labelOther={tr('payment.result.other_calendar')}
                    bookingId={reservation.bookingId}
                    tenantId={tenantId}
                  />
                </div>
              )}
            </>
          ) : (
            <>
              {purchase?.name && (
                <p className="payment-booking-preview-line">
                  <strong>{purchase.name}</strong>
                </p>
              )}
              {purchase?.details && (
                <p className="payment-booking-preview-line">{purchase.details}</p>
              )}
            </>
          )}
        </div>
      )}

      <div className="payment-result-actions" style={{ marginTop: 16 }}>
        {onPrimaryAction && (
          <button
            type="button"
            className="close-button payment-result-actions-primary"
            onClick={onPrimaryAction}
          >
            {primaryLabel}
          </button>
        )}
        {onBackToMenu && (
          <button
            type="button"
            className="close-button payment-result-actions-secondary"
            style={{ marginTop: 8 }}
            onClick={onBackToMenu}
          >
            {secondaryLabel}
          </button>
        )}
      </div>
    </div>
  );
}
